import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Phase 5 — shared workspace + cycle picker.
 * Reads from /v2/dashboard/workspaces + /v2/dashboard/cycles/:eid.
 * Remembers last selection in localStorage.
 */
export function useCyclePicker() {
    const { api, user } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [enterpriseId, setEnterpriseId] = useState(() => localStorage.getItem("dash_enterprise_id") || "");
    const [cycleId, setCycleId] = useState(() => localStorage.getItem("dash_cycle_id") || "");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const loadWorkspaces = async () => {
            try {
                const res = await api.get("/v2/dashboard/workspaces");
                if (cancelled) return;
                const ws = res.data.workspaces || [];
                setWorkspaces(ws);
                if (ws.length) {
                    const preferred = user?.enterprise_id && ws.find((w) => w.id === user.enterprise_id)?.id;
                    const chosen = enterpriseId && ws.find((w) => w.id === enterpriseId) ? enterpriseId : (preferred || ws[0].id);
                    if (chosen !== enterpriseId) {
                        setEnterpriseId(chosen);
                        localStorage.setItem("dash_enterprise_id", chosen);
                    }
                }
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || e.message);
            }
        };
        loadWorkspaces();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!enterpriseId) { setLoading(false); return; }
        let cancelled = false;
        const loadCycles = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/v2/dashboard/cycles/${enterpriseId}`);
                if (cancelled) return;
                const cs = res.data.cycles || [];
                setCycles(cs);
                if (cs.length) {
                    const chosen = cycleId && cs.find((c) => c.id === cycleId) ? cycleId : cs[0].id;
                    if (chosen !== cycleId) {
                        setCycleId(chosen);
                        localStorage.setItem("dash_cycle_id", chosen);
                    }
                } else {
                    setCycleId("");
                }
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadCycles();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enterpriseId]);

    const pickWorkspace = (id) => {
        setEnterpriseId(id);
        localStorage.setItem("dash_enterprise_id", id);
    };
    const pickCycle = (id) => {
        setCycleId(id);
        localStorage.setItem("dash_cycle_id", id);
    };

    return { workspaces, cycles, enterpriseId, cycleId, pickWorkspace, pickCycle, loading, error };
}

export function CyclePickerBar({ picker }) {
    const { workspaces, cycles, enterpriseId, cycleId, pickWorkspace, pickCycle } = picker;

    return (
        <div className="flex flex-wrap items-center gap-3 bg-card border border-border/40 px-4 py-3 mb-6" data-testid="cycle-picker-bar">
            <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Workspace</span>
                <select
                    value={enterpriseId}
                    onChange={(e) => pickWorkspace(e.target.value)}
                    className="bg-black border border-border/40 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-white/30"
                    data-testid="workspace-select"
                >
                    {workspaces.length === 0 && <option value="">No workspaces</option>}
                    {workspaces.map((w) => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                </select>
            </div>
            <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Cycle</span>
                <select
                    value={cycleId}
                    onChange={(e) => pickCycle(e.target.value)}
                    className="bg-black border border-border/40 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-white/30"
                    data-testid="cycle-select"
                >
                    {cycles.length === 0 && <option value="">No cycles</option>}
                    {cycles.map((c) => (
                        <option key={c.id} value={c.id}>{c.name} · {c.status}</option>
                    ))}
                </select>
            </div>
        </div>
    );
}
